/**
 * Drill-down arbre des séquences : joueurs → détail plis → entraînement.
 */

import { useCallback, useEffect, useState } from 'react';
import PlayTreePathList from './PlayTreePathList';
import { toggleExpandedPath } from './playTreePaths';
import { BACKEND_URL } from '../../config';
import DiffereStatsCardChip from './DiffereStatsCardChip';
import DiffereStatsDealDetailModal from './DiffereStatsDealDetailModal';
import type { EquivMode } from './DiffereStatsEquivModeToggle';
import {
  type DiffereStatsDealDetail,
  fetchDiffereStatsDealDetail,
  formatPerDealRankLabel,
} from '../../utils/differeStatsReplay';
import { formatPlayTreeNodeLabel, formatPlayTreePathLabelsFromRoot } from './differeStatsTreeLabel';
import PlayTreeNodeDeciles from './PlayTreeNodeDeciles';
import DiffereStatsDecisionFlow, { type DecisionFlowSelection } from './DiffereStatsDecisionFlow';

export interface PlayTreeRankHighlight {
  kind: 'first' | 'second' | 'third' | 'last';
  label: string;
}

export interface PlayTreeNode {
  key: string;
  label: string;
  depth: number;
  count: number;
  decileCounts: number[];
  rankHighlights: PlayTreeRankHighlight[];
  /** Cartes humaines distinctes observées sur cette branche (top-X). */
  humanCards?: number[];
  /** true = tous entamaient ; false = aucun ; omis = mixte. */
  humanLed?: boolean;
  children: PlayTreeNode[];
}

export interface PlayTreePlayerMatch {
  differeId: number;
  resultId: number;
  dealIndex: number;
  pseudo: string;
  pseudoDisplay: string | null;
  perDealRank: number;
  perDealRankExAequo?: boolean;
  dealScore: number;
  contract: number;
  contractLabel: string;
  isAttackDeal: boolean;
  humanCards: number[];
  humanLed?: boolean[];
}

function adminFetch(url: string, pseudo: string) {
  const separator = url.includes('?') ? '&' : '?';
  return fetch(`${url}${separator}pseudo=${encodeURIComponent(pseudo)}`);
}

function maxCountInTree(nodes: PlayTreeNode[]): number {
  let max = 1;
  for (const n of nodes) {
    max = Math.max(max, n.count, maxCountInTree(n.children));
  }
  return max;
}

function pathPrefixMatch(path: string[], selectedPath: string[]): 'selected' | 'ancestor' | 'none' {
  if (path.join('|') === selectedPath.join('|')) return 'selected';
  if (selectedPath.length > path.length && path.every((k, i) => selectedPath[i] === k)) {
    return 'ancestor';
  }
  return 'none';
}

/** Nœud sur le préfixe consensus (chemin exact jusqu'à consensusDepth). */
function isOnConsensusPath(path: string[], consensusPathKeys: string[]): boolean {
  if (consensusPathKeys.length === 0 || path.length === 0) return false;
  if (path.length > consensusPathKeys.length) return false;
  return path.every((k, i) => consensusPathKeys[i] === k);
}

function PlayTreeNodeCard({
  node,
  path,
  selectedPath,
  onSelectPath,
  onToggleExpand,
  consensusPathKeys = [],
}: {
  node: PlayTreeNode;
  path: string[];
  selectedPath: string[];
  onSelectPath: (path: string[]) => void;
  /** Clic sur la carte : déplier/replier si enfants, sinon liste joueurs. */
  onToggleExpand?: () => void;
  consensusPathKeys?: string[];
}) {
  const match = pathPrefixMatch(path, selectedPath);
  const onConsensus = isOnConsensusPath(path, consensusPathKeys);
  const led = node.humanLed === true;
  return (
    <div className="differe-stats-play-tree-node-card">
      <button
        type="button"
        className={[
          'differe-stats-sankey-node',
          'differe-stats-sankey-node-btn',
          match === 'selected' ? 'active' : '',
          match === 'ancestor' ? 'on-path' : '',
          onConsensus ? 'differe-stats-sankey-node--consensus' : '',
          led ? 'differe-stats-sankey-node--lead' : '',
        ].filter(Boolean).join(' ')}
        title={
          onToggleExpand
            ? `${formatPlayTreeNodeLabel(node)} — ${node.count} joueur(s)${led ? ' — entame' : ''}${onConsensus ? ' — consensus adaptatif' : ''}. Clic : déplier/replier la suite.`
            : `${formatPlayTreeNodeLabel(node)} — ${node.count} joueur(s)${led ? ' — entame' : ''}${onConsensus ? ' — consensus adaptatif' : ''}.`
        }
        onClick={() => {
          if (onToggleExpand) onToggleExpand();
          else onSelectPath(path);
        }}
      >
        <span className="differe-stats-sankey-node-main">
          <span className="differe-stats-sankey-node-label">{formatPlayTreeNodeLabel(node)}</span>
          <span className="differe-stats-sankey-node-count" title={`${node.count} joueur(s)`}>
            {node.count}
          </span>
        </span>
      </button>
      {onToggleExpand && (
        <button
          type="button"
          className="admin-btn admin-btn-sm differe-stats-play-tree-drill-btn"
          title="Lister les joueurs de cette branche"
          onClick={() => onSelectPath(path)}
        >
          Joueurs
        </button>
      )}
      <PlayTreeNodeDeciles node={node} variant="inline" />
    </div>
  );
}

function PlayTreeBranch({
  nodes,
  ancestorPath,
  maxDepth,
  maxCount,
  selectedPath,
  onSelectPath,
  expandedPaths,
  onToggleExpand,
  consensusPathKeys = [],
}: {
  nodes: PlayTreeNode[];
  ancestorPath: string[];
  maxDepth: number;
  maxCount: number;
  selectedPath: string[];
  onSelectPath: (path: string[]) => void;
  expandedPaths: Set<string>;
  onToggleExpand: (pathKey: string) => void;
  consensusPathKeys?: string[];
}) {
  return (
    <ul className="differe-stats-play-tree-level">
      {nodes.map(node => {
        const path = [...ancestorPath, node.key];
        const pathKey = path.join('|');
        const hasChildren = node.children.length > 0 && node.depth < maxDepth;
        const isExpanded = expandedPaths.has(pathKey);
        return (
          <li key={pathKey} className="differe-stats-play-tree-item">
            <div className="differe-stats-play-tree-node-row">
              {hasChildren ? (
                <button
                  type="button"
                  className={`differe-stats-play-tree-expand${isExpanded ? ' expanded' : ''}`}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Replier' : 'Déplier'}
                  title={isExpanded ? 'Replier (et les sous-branches)' : 'Déplier le pli suivant'}
                  onClick={() => onToggleExpand(pathKey)}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              ) : (
                <span className="differe-stats-play-tree-expand differe-stats-play-tree-expand--leaf" aria-hidden />
              )}
              <PlayTreeNodeCard
                node={node}
                path={path}
                selectedPath={selectedPath}
                onSelectPath={onSelectPath}
                onToggleExpand={hasChildren ? () => onToggleExpand(pathKey) : undefined}
                consensusPathKeys={consensusPathKeys}
              />
            </div>
            {hasChildren && isExpanded && (
              <div className="differe-stats-play-tree-children">
                <div className="differe-stats-play-tree-child-label">Pli {node.depth + 1}</div>
                <PlayTreeBranch
                  nodes={node.children}
                  ancestorPath={path}
                  maxDepth={maxDepth}
                  maxCount={maxCount}
                  selectedPath={selectedPath}
                  onSelectPath={onSelectPath}
                  expandedPaths={expandedPaths}
                  onToggleExpand={onToggleExpand}
                  consensusPathKeys={consensusPathKeys}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export type PlayTreeViewMode = 'tree' | 'list' | 'flow';

/**
 * Contexte de la vue Vagues, fourni uniquement en mode donne unitaire : l'écart et
 * le rang sur la donne n'ont pas de sens sur un pool multi-donnes.
 */
export interface PlayTreeFlowContext {
  sessionPseudo: string;
  differeId: number;
  dealIndex: number;
  dealLabel: string;
  topX: number;
  groupEquiv: boolean;
  lowRankLimit: number;
  contractTypes: string;
  selection: DecisionFlowSelection | null;
  onSelect: (selection: DecisionFlowSelection | null) => void;
}

function consensusExpandedPrefixes(consensusPathKeys: string[]): Set<string> {
  const next = new Set<string>();
  for (let i = 1; i <= consensusPathKeys.length; i++) {
    next.add(consensusPathKeys.slice(0, i).join('|'));
  }
  return next;
}

export function PlayTreeSankeyInteractive({
  root,
  maxDepth,
  listDepth,
  selectedPath,
  onSelectPath,
  maxNodesPerLayer,
  pathLabelByKey,
  consensusPathKeys = [],
  consensusDepth = 0,
  consensusBranchSize = 0,
  consensusStopReason,
  flowContext,
}: {
  root: PlayTreeNode;
  /** Profondeur max pour déplier l'arbre interactif. */
  maxDepth: number;
  /** Profondeur des hypothèses en vue liste (défaut = maxDepth). */
  listDepth?: number;
  selectedPath: string[];
  onSelectPath: (path: string[]) => void;
  /** Limite au pli 1 uniquement (vue arbre) ; la liste affiche toutes les hypothèses. */
  maxNodesPerLayer?: number;
  pathLabelByKey?: Map<string, import('./differeStatsTreeLabel').PlayTreePathLabelInfo | string>;
  consensusPathKeys?: string[];
  consensusDepth?: number;
  consensusBranchSize?: number;
  consensusStopReason?: string;
  /** Active l'onglet Vagues ; absent en mode pool. */
  flowContext?: PlayTreeFlowContext;
}) {
  const effectiveListDepth = listDepth ?? maxDepth;
  const [viewMode, setViewMode] = useState<PlayTreeViewMode>('list');
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set());
  const maxCount = maxCountInTree(root.children);
  const pli1 = maxNodesPerLayer != null ? root.children.slice(0, maxNodesPerLayer) : root.children;

  useEffect(() => {
    // Pré-déplie le chemin consensus pour le rendre visible en vue arbre.
    setExpandedPaths(consensusExpandedPrefixes(consensusPathKeys));
  }, [root, consensusPathKeys.join('|')]);

  const handleToggleExpand = useCallback((pathKey: string) => {
    setExpandedPaths(prev => toggleExpandedPath(prev, pathKey));
  }, []);

  return (
    <div className="differe-stats-play-tree-explorer">
      <div className="differe-stats-play-tree-view-tabs">
        <button
          type="button"
          className={`admin-btn admin-btn-sm${viewMode === 'tree' ? ' admin-btn-primary' : ''}`}
          onClick={() => setViewMode('tree')}
        >
          Arbre
        </button>
        <button
          type="button"
          className={`admin-btn admin-btn-sm${viewMode === 'list' ? ' admin-btn-primary' : ''}`}
          onClick={() => setViewMode('list')}
        >
          Liste
        </button>
        {flowContext && (
          <button
            type="button"
            className={`admin-btn admin-btn-sm${viewMode === 'flow' ? ' admin-btn-primary' : ''}`}
            onClick={() => setViewMode('flow')}
            title="Diagramme des décisions : effectif en hauteur, rang sur la donne en ordonnée"
          >
            Vagues
          </button>
        )}
      </div>

      {viewMode === 'flow' && flowContext ? (
        <DiffereStatsDecisionFlow {...flowContext} />
      ) : viewMode === 'tree' ? (
        <div className="differe-stats-play-tree">
          <p className="differe-stats-play-tree-root-label">
            Pli 1 — cliquez ▶ ou la carte pour déplier ; bouton Joueurs pour le drill-down
            {consensusDepth > 0 ? (
              <>
                {' '}
                <span className="differe-stats-consensus-legend">
                  · nœuds consensus (pli {consensusDepth}) mis en évidence
                </span>
              </>
            ) : null}
          </p>
          <PlayTreeBranch
            nodes={pli1}
            ancestorPath={[]}
            maxDepth={maxDepth}
            maxCount={maxCount}
            selectedPath={selectedPath}
            onSelectPath={onSelectPath}
            expandedPaths={expandedPaths}
            onToggleExpand={handleToggleExpand}
            consensusPathKeys={consensusPathKeys}
          />
        </div>
      ) : (
        <PlayTreePathList
          root={root}
          maxDepth={effectiveListDepth}
          selectedPath={selectedPath}
          onSelectPath={onSelectPath}
          pathLabelByKey={pathLabelByKey}
          consensusPathKeys={consensusPathKeys}
          consensusDepth={consensusDepth}
          consensusBranchSize={consensusBranchSize}
          consensusStopReason={consensusStopReason}
        />
      )}
    </div>
  );
}


export default function DiffereStatsPlayTreeDrill({
  sessionPseudo,
  poolId,
  topX,
  depth,
  groupEquiv,
  equivMode,
  equivRequireOthers,
  equivRequireWinner,
  lowRankLimit,
  contractTypes,
  playTreeRoot,
  totalSequences,
}: {
  sessionPseudo: string;
  poolId: number;
  topX: number;
  depth: number;
  groupEquiv: boolean;
  equivMode: EquivMode;
  equivRequireOthers: boolean;
  equivRequireWinner: boolean;
  lowRankLimit: number;
  contractTypes: string;
  playTreeRoot: PlayTreeNode;
  totalSequences: number;
}) {
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [matches, setMatches] = useState<PlayTreePlayerMatch[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [playersError, setPlayersError] = useState<string | null>(null);

  const [studiedDetail, setStudiedDetail] = useState<DiffereStatsDealDetail | null>(null);
  const [compareDetail, setCompareDetail] = useState<DiffereStatsDealDetail | null>(null);
  const [detailHypothesisCards, setDetailHypothesisCards] = useState<number[] | null>(null);
  const [detailTrickFocus, setDetailTrickFocus] = useState<number | null>(null);
  const [detailDiffSeatKeys, setDetailDiffSeatKeys] = useState<Set<string> | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedPath.length === 0) {
      setMatches([]);
      setPlayersError(null);
      return;
    }
    let cancelled = false;
    setLoadingPlayers(true);
    setPlayersError(null);
    const q = new URLSearchParams({
      path: selectedPath.map(k => encodeURIComponent(k)).join(','),
      topX: String(topX),
      depth: String(depth),
      groupEquiv: String(groupEquiv),
      equivRequireOthers: equivRequireOthers ? '1' : '0',
      equivRequireWinner: equivRequireWinner ? '1' : '0',
    });
    if (groupEquiv) q.set('lowRankLimit', String(lowRankLimit));
    void adminFetch(
      `${BACKEND_URL}/api/admin/stats/differe-pool/${poolId}/play-tree/players?${q}`,
      sessionPseudo,
    )
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Erreur chargement joueurs');
        if (!cancelled) setMatches(data.matches ?? []);
      })
      .catch(e => {
        if (!cancelled) {
          setPlayersError(e instanceof Error ? e.message : String(e));
          setMatches([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPlayers(false);
      });
    return () => { cancelled = true; };
  }, [selectedPath, poolId, topX, depth, groupEquiv, equivRequireOthers, equivRequireWinner, lowRankLimit, sessionPseudo]);

  const openDealDetail = useCallback(async (m: PlayTreePlayerMatch) => {
    setLoadingDetail(true);
    setDetailError(null);
    setStudiedDetail(null);
    setCompareDetail(null);
    setDetailDiffSeatKeys(null);
    try {
      const data = await fetchDiffereStatsDealDetail({
        sessionPseudo,
        topX,
        contractTypes,
        equivMode,
        lowRankLimit,
        differeId: m.differeId,
        resultId: m.resultId,
        dealIndex: m.dealIndex,
      });
      setStudiedDetail(data);
      setDetailHypothesisCards(
        selectedPath.length > 0 ? m.humanCards.slice(0, selectedPath.length) : null,
      );
      setDetailTrickFocus(null);
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingDetail(false);
    }
  }, [sessionPseudo, topX, contractTypes, equivMode, lowRankLimit, selectedPath.length]);

  const closeDealDetailModal = useCallback(() => {
    setStudiedDetail(null);
    setCompareDetail(null);
    setDetailHypothesisCards(null);
    setDetailTrickFocus(null);
    setDetailDiffSeatKeys(null);
    setDetailError(null);
  }, []);

  return (
  <>
      <h4>Arbre des séquences (top-{topX}, {totalSequences} observations)</h4>
      <p className="admin-muted differe-stats-hint">
        Arbre conditionnel : les cartes au pli 2 sont imbriquées sous chaque carte du pli 1, etc.
        Cliquez sur un nœud pour lister les joueurs du top-{topX} ayant joué cette suite.
      </p>
      <PlayTreeSankeyInteractive
        root={playTreeRoot}
        maxDepth={depth}
        selectedPath={selectedPath}
        onSelectPath={setSelectedPath}
        maxNodesPerLayer={12}
      />

      {selectedPath.length > 0 && (
        <section className="differe-stats-drill-players admin-card">
          <h5>
            Joueurs — {formatPlayTreePathLabelsFromRoot(playTreeRoot, selectedPath)}
            <button type="button" className="admin-btn admin-btn-sm" onClick={() => setSelectedPath([])}>
              Fermer
            </button>
          </h5>
          {loadingPlayers && <p className="admin-muted">Chargement…</p>}
          {playersError && <p className="admin-error-text">{playersError}</p>}
          {!loadingPlayers && !playersError && matches.length === 0 && (
            <p className="admin-muted">Aucun joueur pour cette séquence.</p>
          )}
          {matches.length > 0 && (
            <div className="differe-stats-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Différé</th>
                    <th>Donne</th>
                    <th>Joueur</th>
                    <th>Rang donne</th>
                    <th>Score</th>
                    <th>Contrat</th>
                    <th>Cartes humaines</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {matches.map(m => (
                    <tr key={`${m.differeId}-${m.resultId}-${m.dealIndex}`}>
                      <td>#{m.differeId}</td>
                      <td>{m.dealIndex + 1}</td>
                      <td>{m.pseudoDisplay || m.pseudo}</td>
                      <td>{formatPerDealRankLabel(m.perDealRank, m.perDealRankExAequo)}</td>
                      <td>{m.dealScore}</td>
                      <td>{m.contractLabel}</td>
                      <td className="differe-stats-top-cells">
                        {m.humanCards.map((c, i) => (
                          <DiffereStatsCardChip key={i} card={c} led={m.humanLed?.[i] === true} />
                        ))}
                      </td>
                      <td>
                        <button type="button" className="admin-btn admin-btn-sm" onClick={() => void openDealDetail(m)}>
                          Détail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <DiffereStatsDealDetailModal
        open={loadingDetail || studiedDetail != null || detailError != null}
        loading={loadingDetail}
        error={detailError}
        studiedDetail={studiedDetail}
        compareDetail={compareDetail}
        hypothesisCards={detailHypothesisCards}
        trickFocus={detailTrickFocus}
        diffSeatKeys={detailDiffSeatKeys}
        sessionPseudo={sessionPseudo}
        topX={topX}
        contractTypes={contractTypes}
        pageEquivMode={equivMode}
        lowRankLimit={lowRankLimit}
        onClose={closeDealDetailModal}
        onStudiedDetail={setStudiedDetail}
        onCompareDetail={setCompareDetail}
        onHypothesisCards={setDetailHypothesisCards}
        onTrickFocus={setDetailTrickFocus}
        onDiffSeatKeys={setDetailDiffSeatKeys}
        onError={setDetailError}
        onLoading={setLoadingDetail}
      />
    </>
  );
}
